import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, X } from 'lucide-react';
import type { User } from '../types';

interface ProfileCompletionBannerProps {
  user: User | null;
}

const ProfileCompletionBanner: React.FC<ProfileCompletionBannerProps> = ({ user }) => {
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);
  const [showThisSession, setShowThisSession] = useState(false);

  // Check if user has avatar or banner was dismissed
  useEffect(() => {
    if (!user || user.avatarUrl) return;

    // Check localStorage for dismissal timestamp
    const dismissedKey = `photo-reminder-dismissed-${user.id}`;
    const lastDismissed = localStorage.getItem(dismissedKey);

    if (lastDismissed) {
      const dismissedDate = new Date(parseInt(lastDismissed));
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);

      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        setIsDismissed(true);
        return;
      }
    }

    // Show banner (not on first page load - wait a few seconds)
    const timer = setTimeout(() => setShowThisSession(true), 2000);
    return () => clearTimeout(timer);
  }, [user]);

  const handleDismiss = () => {
    if (!user) return;
    const dismissedKey = `photo-reminder-dismissed-${user.id}`;
    localStorage.setItem(dismissedKey, Date.now().toString());
    setIsDismissed(true);
  };

  if (!user || user.avatarUrl || isDismissed || !showThisSession) {
    return null;
  }

  return (
    <div className="relative bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-cyan-500/10 border border-pink-500/20 rounded-xl py-3 px-5 shadow-sm mb-6">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1.5 hover:bg-white/10 rounded-full transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-theme-text-secondary hover:text-contrast" />
      </button>

      <div className="text-center max-w-md mx-auto pr-6">
        <h3 className="text-sm font-semibold text-contrast mb-0.5">
          Add a photo to boost your visibility
        </h3>
        <p className="text-xs text-theme-text-secondary mb-3">
          Members with photos get accepted onto more Vybes!
        </p>

        <button
          onClick={() => navigate('/myAccount')}
          className="inline-flex flex-col items-center gap-1 group"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-cyan-500 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all group-hover:scale-105">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <span className="text-xs font-medium text-contrast group-hover:text-pink-500 transition-colors">
            Add photo now
          </span>
        </button>
      </div>
    </div>
  );
};

export default ProfileCompletionBanner;
